"use client";

import { useEffect, useState } from "react";
import { WilsonLoader } from "@/components/WilsonLoader";
import { ErrorState } from "@/components/desktop/ErrorState";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { SignedInSessionProvider } from "@/hooks/useSignedInSession";
import { claimPageForSession, isPageShowingAnotherSession, isSameSession, onRememberedSessionChangeElsewhere, writeLastVerifiedSession, type VerifiedSession } from "@/utils/verifiedSession.utils";
interface AuthGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}
export function AuthGuard({
  children,
  fallback
}: AuthGuardProps) {
  const {
    session,
    error,
    refetch
  } = useCurrentUser({
    shouldFetch: true
  });
  const [sessionNamedDuringLoad, setSessionNamedDuringLoad] = useState<VerifiedSession | null>(null);
  const loading = fallback ?? <div className="flex min-h-screen items-center justify-center">
            <WilsonLoader />
        </div>;
  const isSessionSuperseded = session !== null && (sessionNamedDuringLoad !== null && !isSameSession(sessionNamedDuringLoad, session) || isPageShowingAnotherSession(session));
  const tabSync = <SessionTabSync session={session} isSessionSuperseded={isSessionSuperseded} onSessionNamedDuringLoad={setSessionNamedDuringLoad} />;
  if (!session) {
    if (error && error.data?.code !== "UNAUTHORIZED") {
      return <>
                    {tabSync}
                    <SessionLoadFailed onRetry={() => void refetch()} />
                </>;
    }
    return <>
                {tabSync}
                {loading}
            </>;
  }
  return <>
            {tabSync}
            {isSessionSuperseded ? loading : <SignedInSessionProvider key={`${session.userId}:${session.orgId}`} session={session}>
                    {children}
                </SignedInSessionProvider>}
        </>;
}
function reloadThisPage(): void {
  window.history.replaceState(null, "", window.location.pathname);
  window.location.reload();
}
function SessionLoadFailed({
  onRetry
}: {
  onRetry: () => void;
}) {
  const t = useClientTranslations("auth");
  return <div className="flex min-h-screen items-center justify-center" data-sentry-component="SessionLoadFailed" data-sentry-source-file="AuthGuard.tsx">
            <ErrorState title={t("sessionLoadFailedTitle")} description={t("sessionLoadFailedDescription")} retryLabel={t("tryAgain")} onRetry={onRetry} data-sentry-element="ErrorState" data-sentry-source-file="AuthGuard.tsx" />
        </div>;
}
function SessionTabSync({
  session,
  isSessionSuperseded,
  onSessionNamedDuringLoad
}: {
  session: VerifiedSession | null;
  isSessionSuperseded: boolean;
  onSessionNamedDuringLoad: (session: VerifiedSession) => void;
}) {
  useEffect(() => {
    if (session && isSessionSuperseded) {
      reloadThisPage();
      return;
    }
    if (session) {
      claimPageForSession(session);
      writeLastVerifiedSession(session);
    }
    return onRememberedSessionChangeElsewhere(next => {
      if (next === null) return;
      if (!session) {
        onSessionNamedDuringLoad(next);
        return;
      }
      if (!isSameSession(next, session)) reloadThisPage();
    });
  }, [session?.userId, session?.orgId, isSessionSuperseded, onSessionNamedDuringLoad]);
  return null;
}